import React, { useMemo } from "react";

import bullseyeIcon from "@assets/bullseye.svg";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { prettyLogEvent } from "@internal/analytics";
import {
  type BenchmarkAttributionModelEnum,
  type BenchmarkAttributionWindowEnum,
  type BenchmarkTypeEnum,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";

import {
  useCurrentBenchmarkRouteAtom,
  useCustomBenchmarksByPlatform,
  useNBCalculatedBenchmarksByPlatform,
} from "../atoms";
import {
  PrettyBenchmarkAttributionModelNames,
  PrettyBenchmarkAttributionModelOptionNames,
  PrettyBenchmarkAttributionWindowNames,
} from "../constants";
import { useGetBenchmarkPlatformsToUse, usePlatformBenchmarks } from "../hooks";

import { type ChooseAndSetValidationTargets } from "./benchmark-choose-and-set-modal";
import { BenchmarkHelpFooter } from "./benchmark-help";
import { BenchmarkPlatformMetricsGrid } from "./benchmark-platform-metrics-grid";
import {
  type BenchmarksByPlatform,
  type PlatformBenchmarks,
} from "./custom-benchmarks-modal";

export const BenchmarkReview = ({
  benchmarkType,
  attributionWindow,
  benchmarkTargetsByPlatform,
  closeModal,
  loadingSubmission,
  onSubmit,
  attributionModel,
}: {
  attributionModel: BenchmarkAttributionModelEnum;
  benchmarkType: BenchmarkTypeEnum;
  benchmarkTargetsByPlatform: ChooseAndSetValidationTargets;
  attributionWindow: BenchmarkAttributionWindowEnum;
  closeModal: () => void;
  loadingSubmission: boolean;
  onSubmit: (
    customBenchmarksByPlatform?: BenchmarksByPlatform,
  ) => Promise<void>;
}) => {
  const platformsToUse = useGetBenchmarkPlatformsToUse();
  const { user } = useUser();

  const [, setNBBenchmarksByPlatform] = useNBCalculatedBenchmarksByPlatform();
  const [, setCustomBenchmarksByPlatform] = useCustomBenchmarksByPlatform();
  const [, navigateTo] = useCurrentBenchmarkRouteAtom();

  const { orderedData, loading: loadingBenchmarks } = usePlatformBenchmarks({
    benchmarkTargets: platformsToUse.map((platform) => ({
      attributionModels: [attributionModel],
      benchmarkType,
      target: benchmarkTargetsByPlatform[platform],
      attributionWindow,
      platform,
    })),
  });

  const benchmarksByPlatform = useMemo(
    () =>
      orderedData.reduce<BenchmarksByPlatform>((acc, curr) => {
        if (!acc[curr.platform]) {
          acc[curr.platform] = [];
        }
        acc[curr.platform].push(curr as PlatformBenchmarks);
        return acc;
      }, {} as BenchmarksByPlatform),
    [orderedData],
  );
  const benchmarksToUse = useMemo(() => {
    return orderedData.filter(
      (item) => attributionModel === item.attributionModel,
    );
  }, [attributionModel, orderedData]);

  const loading = loadingSubmission || loadingBenchmarks;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center mr-20">
        <div className="text-lg font-semibold">
          Review your 1-day benchmarks for the{" "}
          <span className="text-primary">
            {PrettyBenchmarkAttributionModelNames[
              attributionModel
            ].toLowerCase()}{" "}
          </span>
          benchmark{" "}
        </div>
        <img
          src={bullseyeIcon}
          alt="bullseye"
          className="ml-3 mb-[0.3rem]"
          style={{
            height: "2rem",
            width: "auto",
          }}
        />
      </div>
      <div className="font-light mt-2 mb-8 mr-20 lg:mr-0 max-w-[42rem] text-sm lg:text-base">
        With your {PrettyBenchmarkAttributionWindowNames[attributionWindow]}{" "}
        {benchmarkType} goals, here are your 1-day buying benchmarks:
      </div>
      <span className="cursor-pointer mr-5 p-3 rounded-2xl border mb-4 w-fit">
        {PrettyBenchmarkAttributionModelOptionNames[attributionModel]}
      </span>
      <BenchmarkPlatformMetricsGrid
        benchmarkTargetsByPlatform={benchmarkTargetsByPlatform}
        benchmarkType={benchmarkType}
        showLabels={false}
        orderedData={benchmarksToUse}
        loading={loading}
        compact={false}
      />
      <div className="2xlh:mt-[2rem] xl:mt-0 flex-grow">
        <div
          className="font-semibold flex cursor-pointer 2xlh:mt-4 mt-0 items-center text-primary"
          onClick={() => {
            if (!loading) {
              setNBBenchmarksByPlatform(benchmarksByPlatform);
              navigateTo("review-with-custom-benchmarks");
              prettyLogEvent.navigateTo({
                user,
                page: Pages.SALES,
                domain: "Benchmarking - Modal",
                additionalActionInfo: "custom-benchmarks",
                data: {
                  benchmarkTargetsByPlatform,
                  benchmarkType,
                  attributionModel,
                  attributionWindow,
                },
              });
            }
          }}
        >
          <span className="underline underline-offset-4">
            I would like to set my own 1 day target goals per each platform
          </span>
          <div className="ml-1 text-[1.1rem]">
            <i className="fa-regular fa-sharp fa-angle-right" />
          </div>
          <GenericTooltip
            content={
              "Set your own benchmarks. We recommend this option " +
              "if you have predefined goals for ad efficiency not reflected in past performance."
            }
          />
        </div>
      </div>
      <BenchmarkHelpFooter>
        <div className="flex items-center">
          <SecondaryButton
            className="mr-2"
            onClick={() => {
              navigateTo("choose-and-set");
              setCustomBenchmarksByPlatform(null);
              prettyLogEvent.goBack({
                user,
                page: Pages.SALES,
                domain: "Benchmarking - Modal",
                additionalActionInfo: "to-choose-and-set",
                data: {
                  benchmarkTargetsByPlatform,
                  benchmarkType,
                  attributionModel,
                  attributionWindow,
                },
              });
            }}
            isDisabled={loading}
          >
            Back
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              onSubmit();
            }}
            isDisabled={loading}
          >
            Apply
          </PrimaryButton>
        </div>
      </BenchmarkHelpFooter>
    </div>
  );
};
